import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import Select from '../../components/Select';
import { findCollectionByKeys, normalizeOptions } from '../../utils/helper';
import { clearEquipmentDetailState, fetchEquipmentServiceDetail } from '../../store/Slices/EquipmentSlice';
import { fetchIssueEquipmentDropdown, fetchIssueTyresDropdown, fetchIssueVehiclesDropdown, fetchIssues, getIssueTagsDropdown, issueLastId } from '../../store/Slices/IssuesSlice';
import { createServiceIssue, clearVehicleDetail, fetchNextServiceId, fetchVehicleDetail } from '../../store/Slices/ServiceSlice';
import { clearTyreDetailState, fetchTyreServiceDetail } from '../../store/Slices/TyreSlice';
import { getUserDropdown } from '../../store/Slices/UserSlice';
import '../../styles/Issue.css';
import { priorityOptions } from '../../utils/Dropdowns';
import { formatUtcDateTimeForApi } from '../../utils/helper';
import { IssueCreateValidationSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import {
    AssetSourceTypeEnum,
    assetTypeOptions,
    extractAssetMeterValue,
    extractLastRecordedMeterValue,
    getPayloadId,
} from './issueAsset.helpers';

const CreateIssue = ({ onClose, searchText, searchFilters, currentPage, limit }) => {
    const dispatch = useDispatch();

    const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));

    const {
        createIssueLoading,
        vehicleDetail,
        nextServiceId,
    } = useSelector((state) => state.services);
    const {
        equipmentDetailData,
    } = useSelector((state) => state.equipment);
    const {
        tyreDetailData,
    } = useSelector((state) => state.tyre);
    const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users);
    const {
        issueLastIdData,
        getIssueTagsDropdownData,
        getIssueTagsDropdownLoading,
        issueVehiclesDropdownData,
        issueVehiclesDropdownLoading,
        issueEquipmentDropdownData,
        issueEquipmentDropdownLoading,
        issueTyresDropdownData,
        issueTyresDropdownLoading,
    } = useSelector((state) => state.issues);

    const [uploadedFiles, setUploadedFiles] = useState([]);

    useEffect(() => {
        dispatch(clearVehicleDetail());
        dispatch(getUserDropdown());
        dispatch(issueLastId());
        dispatch(fetchNextServiceId());
        dispatch(fetchIssueVehiclesDropdown());
        dispatch(fetchIssueEquipmentDropdown());
        dispatch(fetchIssueTyresDropdown());
        dispatch(getIssueTagsDropdown());

        return () => {
            dispatch(clearVehicleDetail());
            dispatch(clearEquipmentDetailState());
            dispatch(clearTyreDetailState());
        };
    }, [dispatch]);

    const resolveDraftServiceId = async () => {
        const normalizedServiceId = getPayloadId(nextServiceId);
        if (
            normalizedServiceId !== '' &&
            normalizedServiceId !== null &&
            normalizedServiceId !== undefined
        ) {
            return normalizedServiceId;
        }

        const response = await dispatch(fetchNextServiceId()).unwrap();
        return getPayloadId(response?.data ?? response ?? 0);
    };

    const getCurrentAssetDetail = (assetType) => {
        if (assetType === AssetSourceTypeEnum.Vehicle) {
            return vehicleDetail;
        }

        if (assetType === AssetSourceTypeEnum.Equipment) {
            return equipmentDetailData;
        }

        if (assetType === AssetSourceTypeEnum.Tyre) {
            return tyreDetailData;
        }

        return null;
    };

    const userOptions = (userDropdownData || []).map(user => ({
        value: user.value,
        label: user.label,
        image: user.image,
        showIcon: true
    }));

    const vehicleOptions = useMemo(() => {
        const source = Array.isArray(issueVehiclesDropdownData)
            ? issueVehiclesDropdownData
            : findCollectionByKeys(issueVehiclesDropdownData, [
                'vehicles',
                'vehicle',
                'vehiclesdropdown',
                'vehicledropdown',
                'vehiclelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'vehicle_id', 'ref_id'],
            labelKeys: ['label', 'registration_number', 'registration_no', 'vehicle_registration_number', 'name', 'title'],
            defaultLabelPrefix: 'Vehicle',
        });
    }, [issueVehiclesDropdownData]);

    const equipmentOptions = useMemo(() => {
        const source = Array.isArray(issueEquipmentDropdownData)
            ? issueEquipmentDropdownData
            : findCollectionByKeys(issueEquipmentDropdownData, [
                'equipments',
                'equipmentdropdown',
                'equipment',
                'equipmentlist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'equipment_id'],
            labelKeys: ['label', 'equipment_id', 'title', 'name', 'serial_number', 'registration_number'],
            defaultLabelPrefix: 'Equipment',
        });
    }, [issueEquipmentDropdownData]);

    const tyreOptions = useMemo(() => {
        const source = Array.isArray(issueTyresDropdownData)
            ? issueTyresDropdownData
            : findCollectionByKeys(issueTyresDropdownData, [
                'tyres',
                'tyre',
                'tyresdropdown',
                'tyredropdown',
                'tyrelist',
            ]);

        return normalizeOptions(source, {
            valueKeys: ['value', 'id', 'tyre_id', 'system_id'],
            labelKeys: ['label', 'tyre_id', 'serial_number', 'system_id', 'name', 'title'],
            defaultLabelPrefix: 'Tyre',
        });
    }, [issueTyresDropdownData]);

    const tagsOptions = (getIssueTagsDropdownData || []).map(tag => ({ label: tag.tag_name, value: tag.tag_name }));

    const handleAssetTypeChange = (selectedOption, setFieldValue) => {
        const nextAssetType = selectedOption?.value || '';

        setFieldValue('asset_type', nextAssetType);
        setFieldValue('asset_id', '');
        setFieldValue('odo_meter', null);
        setFieldValue('odm', '');
        dispatch(clearVehicleDetail());
        dispatch(clearEquipmentDetailState());
        dispatch(clearTyreDetailState());
    };

    const handleAssetChange = async (selectedOption, assetType, setFieldValue) => {
        setFieldValue('asset_id', selectedOption?.value || '');
        setFieldValue('odo_meter', null);
        setFieldValue('odm', '');

        if (!selectedOption) {
            setFieldValue('odo_meter', null);
            setFieldValue('odm', '');
            dispatch(clearVehicleDetail());
            dispatch(clearEquipmentDetailState());
            dispatch(clearTyreDetailState());
            return;
        }

        dispatch(clearVehicleDetail());
        dispatch(clearEquipmentDetailState());
        dispatch(clearTyreDetailState());

        if (assetType === AssetSourceTypeEnum.Vehicle) {
            try {
                const response = await dispatch(fetchVehicleDetail({
                    vehicleId: selectedOption.value,
                    vehicleBrandId: selectedOption.make_id ?? selectedOption.brand_id,
                    ref_id: selectedOption.ref_id
                })).unwrap();
                const detailData = response?.data || {};
                const meterValue = extractAssetMeterValue(detailData);
                const lastRecordedMeter = extractLastRecordedMeterValue(detailData);

                setFieldValue('odo_meter', lastRecordedMeter ?? null);
                setFieldValue('odm', meterValue ?? '');
            } catch (error) {
                setFieldValue('odo_meter', null);
                setFieldValue('odm', '');
                errorNotify(error);
            }

            return;
        }

        try {
            const serviceId = await resolveDraftServiceId();

            if (assetType === AssetSourceTypeEnum.Equipment) {
                const response = await dispatch(fetchEquipmentServiceDetail({
                    equipmentId: selectedOption.value,
                    serviceId,
                })).unwrap();
                const detailData = response?.data || response || {};
                const meterValue = extractAssetMeterValue(detailData);
                const lastRecordedMeter = extractLastRecordedMeterValue(detailData);

                setFieldValue('odo_meter', lastRecordedMeter ?? null);
                setFieldValue('odm', meterValue ?? '');
                return;
            }

            if (assetType === AssetSourceTypeEnum.Tyre) {
                const response = await dispatch(fetchTyreServiceDetail({
                    tyreId: selectedOption.value,
                    serviceId,
                })).unwrap();
                const detailData = response?.data || response || {};
                const meterValue = extractAssetMeterValue(detailData);
                const lastRecordedMeter = extractLastRecordedMeterValue(detailData);

                setFieldValue('odo_meter', lastRecordedMeter ?? null);
                setFieldValue('odm', meterValue ?? '');
            }
        } catch (error) {
            setFieldValue('odo_meter', null);
            setFieldValue('odm', '');
            errorNotify(error);
        }
    };

    const initialValues = {
        asset_type: '',
        asset_id: '',
        service_id: 456,
        reported_by: userDetails?.id || '',
        assign_to: '',
        odo_meter: '',
        odm: '',
        priority: '',
        status: 'open',
        summery: '',
        description: '',
        reported_at: '',
        reported_time: '',
        tags: [],
        files: []
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const issueData = {
                asset_id: getPayloadId(values.asset_id),
                asset_type: values.asset_type,
                reported_by: parseInt(values.reported_by),
                assign_to: parseInt(values.assign_to),
                odo_meter: parseInt(values.odm),
                priority: values.priority,
                status: values.status,
                summery: values.summery,
                description: values.description,
                reported_at: formatUtcDateTimeForApi(values.reported_at),
                tags: values.tags.map(tag => tag.value || tag),
                files: uploadedFiles.map(file => file.fileName)
            };

            await dispatch(createServiceIssue(issueData)).unwrap();
            successNotify('Issue created successfully');
            onClose();
            resetForm();

            if (searchText.length > 0 || searchFilters) {
                dispatch(fetchIssues({
                    page: currentPage,
                    limit: limit,
                    search_text: searchText,
                    searchFilters: searchFilters
                }));
            }
            else {
                dispatch(fetchIssues({
                    page: currentPage,
                    limit: limit,
                }));
            }


        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={IssueCreateValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => {
                const selectedAssetType = values.asset_type || '';
                const currentAssetOptions = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? vehicleOptions
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? equipmentOptions
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? tyreOptions
                            : [];
                const currentAssetLoading = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? issueVehiclesDropdownLoading
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? issueEquipmentDropdownLoading
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? issueTyresDropdownLoading
                            : false;
                const currentAssetLabel = selectedAssetType === AssetSourceTypeEnum.Vehicle
                    ? 'Vehicle'
                    : selectedAssetType === AssetSourceTypeEnum.Equipment
                        ? 'Equipment'
                        : selectedAssetType === AssetSourceTypeEnum.Tyre
                            ? 'Tyre'
                            : 'Asset';
                const currentAssetPlaceholder = selectedAssetType
                    ? `Select ${currentAssetLabel}`
                    : 'Select Asset Type First';
                const currentAssetDetail = getCurrentAssetDetail(selectedAssetType);
                const currentLastRecordedMeter = extractLastRecordedMeterValue(currentAssetDetail);
                const nextIssueId = Number.isFinite(Number(issueLastIdData))
                    ? Number(issueLastIdData) + 1
                    : '0000';

                return (
                    <div>
                    <Row>
                        <Col md={12}>
                            <div className='show_issue_id'>
                                Issue ID: <span>SI-{nextIssueId}</span>
                            </div>
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Assets Type"
                                placeholder="Select Asset Type"
                                options={assetTypeOptions}
                                loading={false}
                                name="asset_type"
                                value={assetTypeOptions.find((option) => option.value === values.asset_type)}
                                onChange={(selectedOption) => handleAssetTypeChange(selectedOption, setFieldValue)}
                                onBlur={handleBlur}
                                error={errors.asset_type}
                                touched={touched.asset_type}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label={currentAssetLabel}
                                placeholder={currentAssetPlaceholder}
                                options={currentAssetOptions}
                                loading={currentAssetLoading}
                                name="asset_id"
                                value={currentAssetOptions.find((option) => String(option.value) === String(values.asset_id)) || null}
                                onChange={(selectedOption) => handleAssetChange(selectedOption, selectedAssetType, setFieldValue)}
                                onBlur={handleBlur}
                                error={errors.asset_id}
                                touched={touched.asset_id}
                                isSearch={true}
                                isDisabled={!selectedAssetType}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Priority"
                                placeholder="Select Priority"
                                options={priorityOptions}
                                loading={false}
                                name="priority"
                                value={priorityOptions.find(option => option.value === values.priority)}
                                onChange={(selectedOption) => setFieldValue('priority', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.priority}
                                touched={touched.priority}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Reported Date & Time"
                                placeholder=""
                                type="datetime-local"
                                name="reported_at"
                                value={values.reported_at}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.reported_at}
                                touched={touched.reported_at}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Summary"
                                placeholder="Brief overview of the issue"
                                type="text"
                                name="summery"
                                value={values.summery}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.summery}
                                touched={touched.summery}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Description (Optional)"
                                placeholder="Add issue description"
                                type="text"
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.description}
                                touched={touched.description}
                                quill={true}
                                setFieldValue={setFieldValue}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Tags"
                                placeholder="Type and press Enter to create tags"
                                options={tagsOptions}
                                loading={getIssueTagsDropdownLoading}
                                name="tags"
                                value={values.tags}
                                onChange={(selectedOptions) => setFieldValue('tags', selectedOptions || [])}
                                onBlur={handleBlur}
                                isMulti={true}
                                isCreatable={true}
                                error={errors.tags && typeof errors.tags === 'string' ? errors.tags : null}
                                touched={touched.tags}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Odometer Reading / Distance"
                                placeholder="Enter Odometer Reading / Distance"
                                type="number"
                                name="odm"
                                showExtraText={currentLastRecordedMeter !== '' && currentLastRecordedMeter !== null && currentLastRecordedMeter !== undefined}
                                extraText={`Last Recorded: ${currentLastRecordedMeter || 0} KM`}
                                value={values.odm}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.odm}
                                touched={touched.odm}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Reported By"
                                placeholder="Please Select"
                                options={userOptions}
                                loading={userDropdownLoading}
                                name="reported_by"
                                value={userOptions.find(option => option.value === values.reported_by)}
                                onChange={(selectedOption) => setFieldValue('reported_by', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.reported_by}
                                touched={touched.reported_by}
                                isSearch={false}
                                isDisabled={true}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Assigned To"
                                placeholder="Please Select"
                                options={userOptions}
                                loading={userDropdownLoading}
                                name="assign_to"
                                value={userOptions.find(option => option.value === values.assign_to)}
                                onChange={(selectedOption) => setFieldValue('assign_to', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.assign_to}
                                touched={touched.assign_to}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <div className='issue_file_upload_wrapper'>
                                <label>Photo & Documents</label>
                                <FileUpload
                                    destination="service-entry"
                                    files={uploadedFiles}
                                    setFiles={setUploadedFiles}
                                    maxFileSize={10}
                                    className='issue_file_upload'
                                />
                            </div>
                        </Col>
                        <Col md={12}>
                            <Button
                                cancelBtn={onClose}
                                submitBtn={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    submitForm();
                                }}
                                loading={createIssueLoading}
                                disabled={createIssueLoading}
                                submitText="Create Issue"
                                type="button"
                            />
                        </Col>
                    </Row>
                    </div>
                );
            }}
        </Formik>
    )
}

export default CreateIssue;
